import React, { useState, useEffect } from 'react';
import { empApi } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Umbrella, Plus, Clock, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const TIPO_LABEL = {
  vacaciones: 'Vacaciones',
  baja_medica: 'Baja médica',
  permiso_retribuido: 'Permiso retribuido',
  ausencia_justificada: 'Ausencia justificada',
  ausencia_injustificada: 'Ausencia injustificada',
  otro: 'Otro',
};

const ESTADO_META = {
  pendiente: { label: 'Pendiente', icon: Clock, box: 'border-amber-500/40 bg-amber-500/10', text: 'text-amber-300' },
  aprobada: { label: 'Aprobada', icon: CheckCircle2, box: 'border-emerald-500/40 bg-emerald-500/10', text: 'text-emerald-300' },
  rechazada: { label: 'Rechazada', icon: XCircle, box: 'border-red-500/40 bg-red-500/10', text: 'text-red-300' },
};

const AusenciasEmpApp = ({ onVolver, onCambioNovedades }) => {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modo, setModo] = useState('lista'); // 'lista' | 'nueva'
  const [form, setForm] = useState({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
  const [enviando, setEnviando] = useState(false);
  const [saldo, setSaldo] = useState(null);

  const cargar = async () => {
    try {
      const [aus, sal] = await Promise.all([
        empApi.get('/empapp/ausencias/mias'),
        empApi.get('/empapp/vacaciones/saldo').catch(() => ({ data: null })),
      ]);
      setLista(aus.data);
      setSaldo(sal.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar');
    } finally { setCargando(false); }
  };

  useEffect(() => {
    cargar();
    // Al abrir esta pantalla marcamos las resueltas como vistas → limpia el badge en Home
    empApi.post('/empapp/ausencias/marcar-vistas').then(() => onCambioNovedades && onCambioNovedades(0)).catch(() => {});
  }, []);

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.fecha_inicio || !form.fecha_fin) { toast.error('Indica las fechas'); return; }
    if (form.fecha_fin < form.fecha_inicio) { toast.error('La fecha "Hasta" no puede ser anterior a "Desde"'); return; }
    setEnviando(true);
    try {
      await empApi.post('/empapp/ausencias/solicitar', form);
      toast.success('Solicitud enviada. Recibirás un email cuando la revisen.');
      setForm({ tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
      setModo('lista');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo enviar'); }
    finally { setEnviando(false); }
  };

  const cancelar = async (id) => {
    if (!window.confirm('¿Cancelar esta solicitud?')) return;
    try {
      await empApi.delete(`/empapp/ausencias/${id}`);
      toast.success('Solicitud cancelada');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo cancelar'); }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="empapp-ausencias-screen">
      <button onClick={onVolver} className="flex items-center gap-2 text-slate-400 mb-6" data-testid="empapp-ausencias-back">
        <ArrowLeft className="h-5 w-5" /> Volver
      </button>
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center">
          <Umbrella className="h-9 w-9 mx-auto mb-2 text-indigo-400" />
          <h2 className="font-heading text-xl font-bold text-white">Mis ausencias</h2>
          <p className="text-slate-400 text-xs mt-1">Solicita vacaciones, permisos o bajas. Tu responsable las aprobará por email.</p>
        </div>

        {modo === 'lista' && (
          <>
            {saldo && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-3" data-testid="empapp-saldo-vacaciones">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Vacaciones {saldo.anio} · {saldo.politica?.tipo_computo === 'naturales' ? 'días naturales' : 'días laborables'}</div>
                <div className="flex items-center gap-3">
                  <div className="text-center flex-1">
                    <div className="text-2xl font-bold text-white tabular-nums" data-testid="empapp-saldo-disponibles">{saldo.disponibles}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-400">Disponibles</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-slate-300 tabular-nums">{saldo.consumidos}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Disfrutadas</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-amber-300 tabular-nums">{saldo.pendientes}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Pendientes</div>
                  </div>
                </div>
              </div>
            )}
            <Button
              onClick={() => setModo('nueva')}
              data-testid="empapp-nueva-ausencia-btn"
              className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 gap-2"
            >
              <Plus className="h-5 w-5" /> Solicitar ausencia
            </Button>

            {cargando ? (
              <div className="text-center text-slate-500 py-8 text-sm">Cargando...</div>
            ) : lista.length === 0 ? (
              <div className="text-center text-slate-500 bg-slate-900 border border-slate-800 rounded-xl py-8 text-sm" data-testid="empapp-sin-ausencias">
                Aún no tienes solicitudes. Pulsa arriba para crear una.
              </div>
            ) : (
              <div className="space-y-2" data-testid="empapp-ausencias-lista">
                {lista.map((a) => {
                  const meta = ESTADO_META[a.estado] || ESTADO_META.pendiente;
                  const Icon = meta.icon;
                  return (
                    <div key={a.id} className={`rounded-xl border ${meta.box} px-4 py-3`} data-testid={`empapp-ausencia-${a.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className={`h-4 w-4 ${meta.text}`} />
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
                        <span className="ml-auto text-xs text-slate-300 font-semibold">{TIPO_LABEL[a.tipo] || a.tipo}</span>
                      </div>
                      <div className="text-white text-sm tabular-nums">
                        {new Date(a.fecha_inicio).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                        {a.fecha_inicio !== a.fecha_fin && ' → ' + new Date(a.fecha_fin).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                      </div>
                      {a.motivo && <div className="text-slate-400 text-xs mt-1">{a.motivo}</div>}
                      {a.comentario_admin && (
                        <div className="mt-2 text-xs bg-white/5 rounded px-2 py-1.5 text-slate-200">
                          <span className="font-semibold">Nota de tu responsable:</span> {a.comentario_admin}
                        </div>
                      )}
                      {a.estado === 'pendiente' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelar(a.id)}
                          className="mt-2 h-7 text-xs bg-transparent border-white/20 text-slate-300 hover:bg-white/5"
                          data-testid={`empapp-cancelar-ausencia-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancelar solicitud
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {modo === 'nueva' && (
          <form onSubmit={enviar} className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="empapp-form-ausencia">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white h-11" data-testid="empapp-ausencia-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Desde</Label>
                <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} required
                  className="bg-slate-950 border-slate-700 text-white h-11" data-testid="empapp-ausencia-desde" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Hasta</Label>
                <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} required
                  className="bg-slate-950 border-slate-700 text-white h-11" data-testid="empapp-ausencia-hasta" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Motivo (opcional)</Label>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} maxLength={500}
                placeholder="Ej. viaje familiar, cita médica..."
                className="bg-slate-950 border-slate-700 text-white h-11" data-testid="empapp-ausencia-motivo" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setModo('lista')} className="flex-1 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">Cancelar</Button>
              <Button type="submit" disabled={enviando} className="flex-1 bg-indigo-500 hover:bg-indigo-600" data-testid="empapp-enviar-solicitud-btn">
                {enviando ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AusenciasEmpApp;
